import React, {useEffect, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {Add, Delete, Edit} from "@mui/icons-material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {
	GET_ADMIN_EXTRACTOR_SETS,
} from "../../components/graphql/queries/gqlAdminSettings";
import {
	DELETE_ADMIN_EXTRACTOR_SET,
	SAVE_ADMIN_EXTRACTOR_SET,
} from "../../components/graphql/mutations/gqlAdminMutations";
import {EXTRACTOR_TYPE_MAP, EXTRACTOR_TYPE_OPTIONS, ExtractorSet} from "../adminTypes";
import {SettingRowEditor, SettingRowData} from "./SettingRowEditor";

export function ExtractorSetsTab() {
	const [sets, setSets] = useState<ExtractorSet[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [editingSet, setEditingSet] = useState<ExtractorSet | null>(null);
	const [saving, setSaving] = useState(false);

	const loadSets = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_EXTRACTOR_SETS,
				fetchPolicy: "no-cache",
			});
			setSets((result.data.adminExtractorSets ?? []).map((s: any) => ({
				...s,
				extractors: (s.extractors ?? []).map((e: any) => ({
					...e,
					extractorType: typeof e.extractorType === "string"
						? (EXTRACTOR_TYPE_MAP[e.extractorType] ?? e.extractorType)
						: e.extractorType,
				})),
			})));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadSets(); 
	}, []);

	const handleEdit = (set: ExtractorSet) => {
		setEditingSet(JSON.parse(JSON.stringify(set)));
	};

	const handleNew = () => {
		setEditingSet({id: 0, code: "", description: "", extractors: []});
	};

	const handleDelete = async (id: number) => {
		if (!window.confirm("Delete this extractor set?")) return;
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: DELETE_ADMIN_EXTRACTOR_SET,
				variables: {input: {id}},
			});
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		}
	};

	const handleSave = async () => {
		if (!editingSet) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_EXTRACTOR_SET,
				variables: {
					input: {
						id: editingSet.id || undefined,
						code: editingSet.code,
						description: editingSet.description,
						extractors: editingSet.extractors.map(e => ({
							id: e.id || undefined,
							order: e.order,
							extractorType: e.extractorType,
							settings: e.settings,
							description: e.description,
						})),
					},
				},
			});
			setEditingSet(null);
			await loadSets();
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	const updateRow = (idx: number, updated: SettingRowData) => {
		if (!editingSet) return;
		const extractors = [...editingSet.extractors];
		extractors[idx] = {
			...extractors[idx],
			order: updated.order,
			extractorType: updated.type,
			settings: updated.settings,
			description: updated.description,
		};
		setEditingSet({...editingSet, extractors});
	};

	if (loading) return <CircularProgress />;

	return (
		<Box sx={{mt: 2}}>
			{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			<Button startIcon={<Add />} variant="contained" onClick={handleNew} sx={{mb: 2}}>
				Add Extractor Set
			</Button>
			{sets.map(set => (
				<Card key={set.id} sx={{mb: 2}}>
					<CardContent>
						<Typography variant="h6">{set.code}</Typography>
						<Typography variant="body2" color="text.secondary">{set.description}</Typography>
						<Typography variant="caption">{set.extractors.length} extractor(s)</Typography>
					</CardContent>
					<CardActions>
						<IconButton onClick={() => handleEdit(set)}><Edit /></IconButton>
						<IconButton color="error" onClick={() => handleDelete(set.id)}><Delete /></IconButton>
					</CardActions>
				</Card>
			))}

			<Dialog open={!!editingSet} onClose={() => setEditingSet(null)} maxWidth="lg" fullWidth>
				<DialogTitle>{editingSet?.id ? "Edit Extractor Set" : "New Extractor Set"}</DialogTitle>
				<DialogContent>
					{editingSet && (
						<Box sx={{mt: 1}}>
							{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
							<TextField fullWidth label="Code" value={editingSet.code}
								onChange={e => setEditingSet({...editingSet, code: e.target.value})}
								sx={{mb: 2}} />
							<TextField fullWidth label="Description" value={editingSet.description}
								onChange={e => setEditingSet({...editingSet, description: e.target.value})}
								sx={{mb: 2}} />
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Order</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Description</TableCell>
										<TableCell>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{[...editingSet.extractors]
										.map((extractor, originalIdx) => ({extractor, originalIdx}))
										.sort((a, b) => a.extractor.order - b.extractor.order)
										.map(({extractor, originalIdx}) => (
											<SettingRowEditor
												key={extractor.id || `new-${originalIdx}`}
												setting={{
													id: extractor.id,
													order: extractor.order,
													type: extractor.extractorType,
													settings: extractor.settings,
													description: extractor.description,
												}}
												typeOptions={EXTRACTOR_TYPE_OPTIONS}
												onChange={updated => updateRow(originalIdx, updated)}
												onDelete={() => setEditingSet({
													...editingSet,
													extractors: editingSet.extractors.filter((_, i) => i !== originalIdx),
												})}
											/>
										))}
								</TableBody>
							</Table>
							<Button startIcon={<Add />} sx={{mt: 1}} onClick={() =>
								setEditingSet({
									...editingSet,
									extractors: [...editingSet.extractors, {
										id: 0,
										order: editingSet.extractors.length + 1,
										extractorType: 0,
										settings: "{}",
										description: null,
									}],
								})
							}>
								Add Extractor
							</Button>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditingSet(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}